import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FiMail, FiArrowRight, FiArrowLeft, FiShield } from "react-icons/fi";
import { NGROK } from "../../../APIs";
import AuthenticationPage from "./components/AuthenticationPage";
import UserService from "../../../services/UserService";
import { myLocalStorage } from "../../../components/StorageHelper";
import { GoPasskeyFill } from "react-icons/go";
import { IoMdUnlock } from "react-icons/io";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // Method to get tenant info from SignUpForm
  const getTenantsInfo = async (email) => {
    const axiosInstance = axios.create();
    try {
      const tenants = await axiosInstance.get(
        `${NGROK}/api/get-tenants?email=${email.toLowerCase().trim()}`
      );
      return tenants;
    } catch (error) {
      return null;
    }
  };

  // Method to check if user exists from SignUpForm
  const checkIfUserExist = async (email) => {
    const tenants = await getTenantsInfo(email.toLowerCase().trim());

    if (!tenants || !tenants.data?.length) {
      return false;
    } else if (tenants?.data?.length) {
      myLocalStorage.setItem("role", tenants?.data[0].role);
      setFirstName(tenants?.data[0].firstName);
      setLastName(tenants?.data[0].lastName);
      return tenants?.data[0];
    }
  };

  // Method to initialize keycloak and register from SignUpForm
  const keycloakInitAndRegister = async (realm) => {
    try {
      setLoading(true);
      myLocalStorage.setItem("realm", realm);
      UserService.initKeycloak(realm);

      setTimeout(() => {
        UserService.doRegister(email.toLowerCase().trim(), firstName, lastName,`${NGROK}/loading`);

      }, 2000);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  // Sign in with passkey handler from SignUpForm
  const handlePasskeySignIn = async () => {
    setLoading(true);
    setError("");
    try {
      const user = await checkIfUserExist(email.toLowerCase().trim());

      if (
        user?.legacyRealm?.realmName === "whiteswan_tenants_initial" ||
        user?.realm?.keycloakAccountExists
      ) {
        setError("User with this email already exists.");
        setLoading(false);
        return;
      }

      if (!user) {
        myLocalStorage.setItem("role", "TENANT_ADMIN");
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        myLocalStorage.setItem("firstName", firstName);
        myLocalStorage.setItem("LastName", lastName);
        await keycloakInitAndRegister("whiteswan_tenants");
      } else if (
        user &&
        user.isInvitedAdmin &&
        !user.realm.keycloakAccountExists
      ) {
        myLocalStorage.setItem("role", "TENANT_ADMIN");
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        myLocalStorage.setItem("firstName", firstName);
        myLocalStorage.setItem("LastName", lastName);
        await keycloakInitAndRegister("whiteswan_tenants");
      } else if (user && !user.realm.keycloakAccountExists) {
        myLocalStorage.setItem("role", "TENANT_USER");
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        myLocalStorage.setItem("firstName", firstName);
        myLocalStorage.setItem("LastName", lastName);
        await keycloakInitAndRegister(user.tenantName);
      }
    } catch (err) {
      setError("Unable to sign in with passkey. Please try again.");
      setLoading(false);
    }
  };

  // Sign in with password & OTP handler from SignUpForm
  const handlePasswordOTPSignIn = async () => {
    setLoading(true);
    setError("");
    try {
      const user = await checkIfUserExist(email.toLowerCase().trim());

      if (
        user?.legacyRealm?.realmName === "whiteswan_tenants_initial" ||
        user?.legacyRealm?.keycloakAccountExists
      ) {
        setError("User with this email already exists.");
        setLoading(false);
        return;
      }

      if (!user) {
        myLocalStorage.setItem("role", "TENANT_ADMIN");
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        myLocalStorage.setItem("firstName", firstName);
        myLocalStorage.setItem("LastName", lastName);
        await keycloakInitAndRegister("whiteswan_tenants_legacy");
      } else if (
        user &&
        user.isInvitedAdmin &&
        !user.realm.keycloakAccountExists
      ) {
        myLocalStorage.setItem("role", "TENANT_ADMIN");
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        myLocalStorage.setItem("firstName", firstName);
        myLocalStorage.setItem("LastName", lastName);
        await keycloakInitAndRegister("whiteswan_tenants_legacy");
      } else if (
        user &&
        !user.isInvitedAdmin &&
        !user.legacyRealm.keycloakAccountExists
      ) {
        myLocalStorage.setItem("role", "TENANT_USER");
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        myLocalStorage.setItem("firstName", firstName);
        myLocalStorage.setItem("lastName", lastName);
        await keycloakInitAndRegister(`${user.legacyRealm.realmName}`);
      }
    } catch (err) {
      setError("Unable to sign in with password & OTP. Please try again.");
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      let response = await axios.post(`${NGROK}/api/auth/forgot-password/send`, {
        email: email.trim().toLowerCase(),
      });
      if (response.data) {
        setStep(2);
      } else {
        setError("Unable to verify the email address. Please try again.");
      }
      
    } catch (err) {
      setError(
        err?.response?.data?.message || "Unable to verify the email address. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      let response = await axios.post(`${NGROK}/api/auth/forgot-password/verify`, {
        email: email.trim().toLowerCase(),
        verificationCode: otp,
      });
       if (response.data) {
        setStep(3);
      } else {
        setError("Invalid or expired code. Please try again.");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || "Invalid or expired code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    let initials = "";
    const nameParts = name.split(" ");

    if (nameParts.length >= 2) {
      initials = `${nameParts[0][0]}${nameParts[1][0]}`;
    } else if (name.includes("@")) {
      const emailParts = name.split("@")[0].split(".");
      if (emailParts.length >= 2) {
        initials = `${emailParts[0][0]}${emailParts[1][0]}`;
      } else {
        initials = `${name[0]}${name[1]}`;
      }
    } else {
      initials = `${name[0]}${name[1]}`;
    }

    return initials.toUpperCase();
  };

  const stringToColor = (name) => {
    let hash = 0;
    let i;

    for (i = 0; i < name.length; i += 1) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
  };

  const goToSignIn = () => navigate("/signIn");

  return (
    <AuthenticationPage>
      <div className="w-full max-w-md px-4">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-blue-500 to-blue-600"></div>
          <div className="p-6 flex flex-col min-h-[520px]">
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-lg mb-3">
                <FiShield className="text-white text-2xl" />
              </div>
              <h1 className="text-xl font-bold  mb-1">
                {step === 1 && "Forgot Password"}
                {step === 2 && "Verify Code"}
                {step === 3 && "Recovery Options"}
              </h1>
              <p className="text-xs text-gray-600">
                {step === 1 && "Enter your account email to get a code."}
                {step === 2 && "Enter the code sent to your email."}
                {step === 3 && "Choose how you want to sign back in."}
              </p>
            </div>

            <div className="flex-1 flex flex-col gap-4">
              {step === 1 && (
                <form onSubmit={handleEmailSubmit} className="flex-1 flex flex-col gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FiMail className="text-gray-400" />
                      </div>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="block w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:bg-gray-100"
                        required
                      />
                      <button
                        type="submit"
                        disabled={!email || loading}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <div className="bg-blue-100 hover:bg-blue-200 rounded-lg p-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                          {loading ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                          ) : (
                            <FiArrowRight className="text-blue-600 text-lg" />
                          )}
                        </div>
                      </button>
                    </div>
                  </div>
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm animate-slideDown">
                      {error}
                    </div>
                  )}
                </form>
              )}

              {step === 2 && (
                <form onSubmit={handleOtpSubmit} className="flex-1 flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-all"
                    >
                      <FiArrowLeft className="text-gray-600" />
                    </button>
                    <span className="text-xs text-gray-600">{email}</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      6-digit code
                    </label>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      maxLength={6}
                      placeholder="Enter code"
                      className="block w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!otp || loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-xl font-semibold transition-all disabled:opacity-60"
                  >
                    {loading ? "Verifying..." : "Verify code"}
                  </button>
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm animate-slideDown">
                      {error}
                    </div>
                  )}
                </form>
              )}

              {step === 3 && (
                <div className="flex-1 flex flex-col gap-3">

                   <div className="flex flex-col items-center gap-3 mb-4">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg"
                    style={{ backgroundColor: stringToColor(email) }}
                  >
                    {getInitials(email)}
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-semibold text-gray-800">
                      Hello, {firstName} {lastName}
                    </p>
                    <p className="text-sm text-gray-600">{email}</p>
                  </div>
                  </div>
                  
                  
                  <button
                    onClick={handlePasskeySignIn}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <GoPasskeyFill className="text-xl" />
                    {loading ? "Processing..." : "Sign in with passkey"}
                  </button>
                  <button
                    onClick={handlePasswordOTPSignIn}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 border-2 border-blue-200 text-blue-700 py-3 rounded-xl font-semibold hover:border-blue-500 hover:bg-blue-50 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <IoMdUnlock className="text-xl" />
                    {loading ? "Processing..." : "Sign in with password & OTP"}
                  </button>
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm animate-slideDown">
                      {error}
                    </div>
                  )}
                  <button
                    onClick={() => setStep(1)}
                    disabled={loading}
                    className="text-sm text-blue-600 hover:text-blue-700 hover:underline text-center disabled:opacity-60"
                  >
                    Try another email
                  </button>
                </div>
              )}
            </div>

            <div className="mt-6 space-y-2 text-center text-sm text-gray-600">
              <p>
                Remembered your password?{" "}
                <button
                  className="text-blue-600 font-semibold hover:underline"
                  onClick={goToSignIn}
                >
                  Back to sign in
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </AuthenticationPage>
  );
};

export default ForgotPassword;
